/**
 * Swiper-based replacement for the product detail page's MAIN image viewer
 * (previously native scroll-snap driven by the `slider-component` custom
 * element). Only used when section.settings.gallery_layout == 'thumbnail_slider'
 * (see snippets/product-media-gallery.liquid) - other layouts keep using
 * slider-component untouched.
 *
 * Unlike the thumbnail strip, the viewer's prev/next buttons stay wired
 * through media-gallery.js's own switchActiveMedia (so is-active class,
 * thumbnail sync, deferred-media loading and live-region announcements all
 * keep working); this script only owns drag/swipe input and reflects it back
 * into media-gallery.js via setActiveMedia. Exposes
 * `root.viewerSwiper = { swiper, slideToActive(item) }`.
 */
(() => {
  const SELECTOR = "[data-viewer-swiper]";

  function updateDots(root, activeIndex) {
    const dots = root.querySelectorAll(".slider-counter__link");
    dots.forEach((dot, i) => {
      dot.classList.toggle("slider-counter__link--active", i === activeIndex);
    });
    const current = root.querySelector(".slider-counter--current");
    if (current) current.textContent = String(activeIndex + 1);
  }

  function updateNavState(swiper, prevBtn, nextBtn) {
    if (prevBtn) prevBtn.disabled = swiper.isBeginning;
    if (nextBtn) nextBtn.disabled = swiper.isEnd;
  }

  function createSwiper(root) {
    const viewport = root.querySelector(".product__media-list.swiper");
    if (!viewport || viewport.querySelectorAll(".swiper-slide").length === 0) {
      return null;
    }

    const prevBtn = root.querySelector('button[name="previous"]');
    const nextBtn = root.querySelector('button[name="next"]');

    return new window.Swiper(viewport, {
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      watchOverflow: true,
      grabCursor: true,
      observer: true,
      observeParents: true,
      on: {
        init: (swiper) => {
          updateNavState(swiper, prevBtn, nextBtn);
          updateDots(root, swiper.activeIndex);
        },
        slideChange: (swiper) => {
          updateNavState(swiper, prevBtn, nextBtn);
          updateDots(root, swiper.activeIndex);

          const activeSlide = swiper.slides[swiper.activeIndex];
          const mediaGallery = root.closest("media-gallery");
          if (mediaGallery && activeSlide?.dataset.mediaId) {
            mediaGallery.setActiveMedia(activeSlide.dataset.mediaId, false);
          }
        },
      },
    });
  }

  function bind(root) {
    if (root.dataset.viewerSwiperBound) return;
    root.dataset.viewerSwiperBound = "true";
    if (typeof window.Swiper === "undefined") return;

    const swiper = createSwiper(root);

    root.viewerSwiper = {
      get swiper() {
        return swiper;
      },
      slideToActive(item) {
        if (!swiper || !item) return;
        const index = Array.prototype.indexOf.call(swiper.slides, item);
        if (index === -1 || index === swiper.activeIndex) return;
        swiper.slideTo(index);
      },
    };
  }

  function apply(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(bind);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => apply());
  } else {
    apply();
  }

  document.addEventListener("shopify:section:load", (event) => apply(event.target));
})();
